"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DatePickerInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _formlabel = require("../../formlabel");
var _DatePickerDialog = require("../dialog/components/DatePickerDialog");
var _useDatePickerDialog = require("../dialog/hooks/useDatePickerDialog");
var _datePickerTheme = require("../dialog/styled/datePickerTheme");
var _DatePickerCatalogs = require("../i18n/DatePickerCatalogs");
var _AtomicDatePickerInput = require("./components/AtomicDatePickerInput");
var _utils = require("./utils");
const DatePickerInputBase = /*#__PURE__*/(0, _react.forwardRef)((_ref, parentInputRef) => {
  let {
    clientId,
    dayIsDisabled,
    format,
    id: providedInputId,
    i18n,
    initialPickerDate,
    label,
    locale,
    maxDate,
    minDate,
    isRequired,
    onValidationStateChange,
    onChange,
    value,
    portalProps,
    width = "compact",
    description,
    descriptionProps,
    "aria-describedby": ariaDescribedBy,
    dialogPopperProps,
    ...rest
  } = _ref;
  const errorMessages = (0, _react.useMemo)(() => {
    return {
      isInvalid: i18n._( /*i18n*/
      {
        id: "Please enter a valid date ({format})",
        values: {
          format
        },
        comment: "An error message showing the user has entered a date in the wrong format. It shows the correct format in parenthesis."
      }),
      isBefore: i18n._( /*i18n*/{
        id: "Please select a date after {minDate}",
        values: {
          minDate: (0, _utils.dateToInputString)(minDate, format)
        },
        comment: "An error message showing the user has entered a date before the required minimum date."
      }),
      isAfter: i18n._( /*i18n*/{
        id: "Please select a date before {maxDate}",
        values: {
          maxDate: (0, _utils.dateToInputString)(maxDate, format)
        },
        comment: "An error message showing the user has entered a date after the required maximum date."
      }),
      isDisabled: i18n._( /*i18n*/{
        id: "The selected date is disabled",
        comment: "An error message showing the user has entered a date that is not allowed because it is disabled."
      }),
      isRequired: i18n._( /*i18n*/{
        id: "Please enter a valid date ({format})",
        values: {
          format
        },
        comment: "An error message telling the user they need to enter a date since it is required. It shows the correct format in parenthesis."
      })
    };
  }, [format, i18n, maxDate, minDate]);
  const inputId = (0, _react.useMemo)(() => providedInputId || (0, _lodestarUtils.genId)("dp"), [providedInputId]);
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const [errorMessage, setErrorMessage] = (0, _react.useState)(null);
  const [pickerIsOpen, setPickerIsOpen] = (0, _react.useState)(false);
  const calendarButtonRef = (0, _react.useRef)(null);

  /**
   * @param refocusTarget if true, manually focus the target again. We cannot rely on
   *                      Flyout here since we force the target ref to be the input.
   */
  const closePicker = refocusTarget => {
    setPickerIsOpen(false);
    if (refocusTarget) {
      requestAnimationFrame(() => {
        calendarButtonRef.current?.focus();
      });
    }
  };
  const openPicker = () => {
    setPickerIsOpen(true);
  };
  const onPickerChange = val => {
    closePicker(true);
    onChange(val);
  };
  const onValidationChange = validationResult => {
    setErrorMessage(validationResult === "isValid" ? null : errorMessages[validationResult]);
    onValidationStateChange?.(validationResult);
  };
  const {
    getTargetHTMLProps,
    getPickerProps
  } = (0, _useDatePickerDialog.useDatePickerDialog)({
    clientId: (0, _lodestarUtils.getClientId)(clientId, "dp-dialog"),
    dayIsDisabled: dayIsDisabled,
    initialPickerDate: initialPickerDate,
    isOpen: pickerIsOpen,
    locale,
    maxDate,
    minDate,
    onChange: onPickerChange,
    onClear: () => {
      onPickerChange(null);
    },
    onCloseRequested: closePicker,
    onOpenRequested: openPicker,
    placement: "bottom-end",
    offset: {
      distance: _datePickerTheme.datePickerTheme.menuOffset[themeMode]
    },
    portalProps,
    // We'll manage the focus ourselves.
    refocusTargetOnClose: false,
    value
  });
  const {
    ref: targetRefFromHook,
    ...targetProps
  } = getTargetHTMLProps({
    onClick: openPicker
  });
  const calendarRef = (0, _lodestarUtils.useForkRef)(calendarButtonRef, targetRefFromHook);
  return /*#__PURE__*/_react.default.createElement(_formlabel.FormLabel, {
    label: label,
    id: inputId,
    validationState: errorMessage ? "error" : undefined,
    validationMessage: errorMessage || undefined,
    description: description || undefined,
    descriptionProps: description ? descriptionProps : undefined,
    "aria-describedby": ariaDescribedBy
  }, labelProps => /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_AtomicDatePickerInput.AtomicDatePickerInput, (0, _extends2.default)({}, rest, labelProps, {
    calendarButtonProps: {
      ...targetProps,
      ref: calendarRef
    },
    onValidationChange: onValidationChange,
    clientId: clientId,
    dayIsDisabled: dayIsDisabled,
    format: format,
    maxDate: maxDate,
    minDate: minDate,
    isRequired: isRequired,
    onChange: onChange,
    ref: parentInputRef,
    value: value,
    isCompact: width === "compact"
  })), /*#__PURE__*/_react.default.createElement(_DatePickerDialog.DatePickerDialog, (0, _extends2.default)({}, getPickerProps(), {
    onCloseRequested: closePicker,
    popperProps: dialogPopperProps
  }))));
});
DatePickerInputBase.displayName = "DatePickerInputBase";
const DatePickerInput = /*#__PURE__*/(0, _react.forwardRef)((_ref2, ref) => {
  let {
    ...props
  } = _ref2;
  (0, _DatePickerCatalogs.injectCatalog)();
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: props.locale
  }, /*#__PURE__*/_react.default.createElement(_i18n.I18NContextConsumer, null, _ref3 => {
    let {
      i18n
    } = _ref3;
    return /*#__PURE__*/_react.default.createElement(DatePickerInputBase, (0, _extends2.default)({}, props, {
      i18n: i18n,
      ref: ref
    }));
  }));
});
exports.DatePickerInput = DatePickerInput;
DatePickerInput.displayName = "DatePickerInput";
//# sourceMappingURL=DatePickerInput.js.map